/* eslint camelcase: 0 */
import _ from 'lodash';
import api, { rawClient } from './api';
import qs from 'query-string';
import {
    ContractStatus,
    DerivedContractStatus,
} from '../constants/digitalSigning';
import {
    ContractSnapshot,
    ContractEventSnapshot,
    ActionPlanSnapshot,
    CreateSignerParams,
    SignerSnapshot,
} from '../models';
import { decamelizeKeys, fromOptionToNullable, keyBy } from '../lib';
import { ArchivalAction } from '../constants/filter';
import {
    CountResponse,
    PaginatedRequest,
    PaginatedResponse,
} from '../lib/pagination';
import { NonPurchasableContractSnapshot } from '../models/DigitalSigning/NonPurchasableContract';
import { getBackVersion } from '../config';

type ActionPlanResponse = {
    availablePurchasePlan: ActionPlanSnapshot;
};

type RawContractResponse = Omit<
    ContractSnapshot,
    'signers' | 'availablePurchasePlan'
> & {
    signers: SignerSnapshot[];
} & ActionPlanResponse;

type ContractResponse = Omit<ContractSnapshot, 'availablePurchasePlan'> &
    ActionPlanResponse;

type RawContractPaginatedResponse = {
    contracts: RawContractResponse[];
} & PaginatedResponse;

type ContractPaginatedResponse = {
    contracts: ContractResponse[];
} & PaginatedResponse;

type ContractRequestFilter = {
    filename?: string;
    name?: string;
    cpf?: string;
    status?: ContractStatus[];
    derivedStatus?: DerivedContractStatus[];
    isArchived?: boolean;
};

type UploadedFilesRequest = {
    s3Path: string;
    filename: string;
};

type Validation = {
    isSupported: boolean;
    isValid: boolean;
    hash: string;
    ts: string;
    documentPassword?: string;
    filename?: string;
    documentId?: string;
    contractId?: string;
    expiresAt?: string;
};

const convertContract = ({
    signers,
    ...response
}: RawContractResponse): ContractResponse => {
    return {
        ...response,
        signers: keyBy(signers, x => x.id),
        loadedSigners: true,
    };
};

const mapWithNestedParties = (response: RawContractResponse[]) =>
    response.map(convertContract);

const getContracts = (): Promise<ContractResponse[]> =>
    api
        .request<RawContractResponse[]>({
            method: 'GET',
            url: `${getBackVersion()}/agent/digital_signing`,
        })
        .then(mapWithNestedParties);

const getContract = (id: ContractSnapshot['id']): Promise<ContractResponse> =>
    api
        .request<RawContractResponse>({
            method: 'GET',
            url: `${getBackVersion()}/agent/digital_signing/` + id,
        })
        .then(convertContract);

const getPaginatedContracts = (
    params: PaginatedRequest & ContractRequestFilter = {}
): Promise<ContractPaginatedResponse> => {
    return api
        .request<RawContractPaginatedResponse>({
            method: 'GET',
            url: `${getBackVersion()}/agent/digital_signing`,
            params: decamelizeKeys({ ...params }),
            paramsSerializer: params => {
                // {a: [2, 3]} -> ?a=2&a=3
                // which is the supported format by the backend
                return qs.stringify(params, { arrayFormat: 'none' });
            },
        })
        .then(response => ({
            contracts: mapWithNestedParties(response.contracts),
            after: response.after,
            count: response.count,
        }));
};

const getContractsCount = (
    params: PaginatedRequest & ContractRequestFilter
): Promise<CountResponse> => {
    return api.request<CountResponse>({
        method: 'GET',
        url: `${getBackVersion()}/agent/digital_signing/count`,
        params: decamelizeKeys(params),
    });
};

const createContract = (
    contractName: string,
    uploadedFiles: UploadedFilesRequest[],
    actionPlanId: number
): Promise<ContractResponse> =>
    api
        .request<RawContractResponse>({
            method: 'POST',
            url: `${getBackVersion()}/agent/digital_signing`,
            data: {
                documents: uploadedFiles.map(uploadedFile => {
                    return {
                        s3_path: uploadedFile.s3Path,
                        filename: uploadedFile.filename,
                    };
                }),
                contract_name: contractName,
                action_plan_id: actionPlanId,
            },
        })
        .then(convertContract);

const patchContract = (
    id: ContractSnapshot['id'],
    status: ContractStatus,
    actionPlanId?: number
): Promise<ContractResponse> =>
    api
        .request<RawContractResponse>({
            method: 'PATCH',
            url: `${getBackVersion()}/agent/digital_signing/` + id,
            data: { status, action_plan_id: actionPlanId },
        })
        .then(convertContract);

const archiveContract = (
    id: ContractSnapshot['id'],
    action: ArchivalAction
): Promise<void> =>
    api.request({
        method: 'DELETE',
        url: `${getBackVersion()}/agent/digital_signing/` + id,
        params: { action },
    });

const getEvents = (
    id: ContractSnapshot['id']
): Promise<ContractEventSnapshot[]> =>
    api
        .request<ContractEventSnapshot[]>({
            method: 'GET',
            url: `${getBackVersion()}/agent/digital_signing/` + id + '/events',
        })
        .then(events => _.sortBy(events, ['occurredAt']));

const createSigner = (
    contractId: ContractSnapshot['id'],
    { name, email, phone, role }: CreateSignerParams
): Promise<SignerSnapshot> => {
    return api.request<SignerSnapshot>({
        method: 'POST',
        url:
            `${getBackVersion()}/agent/digital_signing/` +
            contractId +
            '/signer',
        data: {
            name: fromOptionToNullable(name),
            email,
            phone: fromOptionToNullable(phone),
            sign_as: role,
        },
    });
};

const removeSigner = (
    contractId: ContractSnapshot['id'],
    signerId: SignerSnapshot['id']
): Promise<void> => {
    return api.request<void>({
        method: 'DELETE',
        url:
            `${getBackVersion()}/agent/digital_signing/` +
            contractId +
            '/signer/' +
            signerId,
    });
};

const askSignAgain = (
    contractId: ContractSnapshot['id'],
    signerId: SignerSnapshot['id']
): Promise<SignerSnapshot> => {
    return api.request<SignerSnapshot>({
        method: 'PATCH',
        url:
            `${getBackVersion()}/agent/digital_signing/` +
            contractId +
            '/signer/' +
            signerId,
    });
};

const getSigners = (
    contractId: ContractSnapshot['id']
): Promise<SignerSnapshot[]> => {
    return api.request<SignerSnapshot[]>({
        method: 'GET',
        url:
            `${getBackVersion()}/agent/digital_signing/` +
            contractId +
            '/signer',
    });
};

const getExternalContract = (
    contractId: ContractSnapshot['id'],
    signerId?: SignerSnapshot['id']
): Promise<NonPurchasableContractSnapshot> => {
    return api
        .request<NonPurchasableContractSnapshot>({
            method: 'GET',
            url: `${getBackVersion()}/external/digital_signing/${contractId}`,
            params: { signer_id: signerId },
        })
        .then(({ signers, ...response }: NonPurchasableContractSnapshot) => ({
            ...response,
            signers: keyBy(signers as unknown as SignerSnapshot[], x => x.id),
            loadedSigners: true,
        }));
};

const getExternalSignerId = (
    contractId: ContractSnapshot['id'],
    signerEmail: SignerSnapshot['email']
): Promise<string> => {
    return api
        .request<{ signerId: string }>({
            method: 'GET',
            url: `${getBackVersion()}/external/digital_signing/${contractId}/signer`,
            params: { signer_email: signerEmail },
        })
        .then(response => response.signerId);
};

const validate = (hash: string): Promise<Validation> => {
    return api.request<Validation>({
        method: 'GET',
        url: `${getBackVersion()}/external/digital_signing/validate`,
        params: { hash: hash },
    });
};

const validateByPassword = (password: string): Promise<Validation> => {
    return api.request<Validation>({
        method: 'GET',
        url: `${getBackVersion()}/external/digital_signing/validate_by_password`,
        params: { document_password: password },
    });
};

const generatePassword = (hash: string): Promise<Validation> => {
    return api.request<Validation>({
        method: 'GET',
        url: `${getBackVersion()}/external/digital_signing/generate_password`,
        params: { hash: hash },
    });
};

const requestNotification = (
    contractId: ContractSnapshot['id'],
    signerId: SignerSnapshot['id']
): Promise<SignerSnapshot> => {
    return api.request<SignerSnapshot>({
        method: 'POST',
        url: `${getBackVersion()}/agent/digital_signing/${contractId}/signer/${signerId}/notifications`,
    });
};

export const getDownloadUrlByPassword = (
    contractId: string,
    documentId: string,
    filename: string,
    documentPassword?: string
): Promise<void> => {
    return rawClient.download({
        url: `${getBackVersion()}/external/digital_signing/${contractId}/documents/${documentId}/download`,
        params: {
            download: true,
            filename: filename,
            document_password: documentPassword,
        },
    });
};

export default {
    archiveContract,
    createContract,
    generatePassword,
    getContract,
    getContracts,
    getContractsCount,
    getEvents,
    getPaginatedContracts,
    patchContract,
    getSigners,
    removeSigner,
    askSignAgain,
    createSigner,
    getExternalContract,
    getExternalSignerId,
    getDownloadUrlByPassword,
    validate,
    validateByPassword,
    requestNotification,
};
export type {
    ContractResponse,
    RawContractPaginatedResponse,
    ContractPaginatedResponse,
    Validation,
};
